import outdent from "outdent";
import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const environments = {
  global: {
    loginDomain: "login.microsoftonline.com",
    apiDomain: "api.securitycenter.microsoft.com",
  },
  "usgov-l4": {
    loginDomain: "login.microsoftonline.com",
    apiDomain: "api-gcc.securitycenter.microsoft.us",
  },
  "usgov-l5": {
    loginDomain: "login.microsoftonline.us",
    apiDomain: "api-gov.securitycenter.microsoft.us",
  },
} as const;
type Environment = keyof typeof environments;

export const microsoftDefenderForEndpoint = defineConnector({
  identifier: "microsoftDefenderForEndpoint",
  name: "Microsoft Defender for Endpoint",
  tagLine: "Endpoint detection and response",
  keywords:
    "mde mdatp windows defender atp advanced threat protection edr xdr security center machines",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: () =>
    "^https://api(?:-(?:gcc|gov))?\\.securitycenter\\.microsoft\\.(?:com|us)/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Create an Azure app registration",
      items: [
        {
          kind: "markdown",
          content: outdent`
            1. Navigate to your [Azure app registrations](https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade) and select **New Registration**
            2. **Name**: Give your app a descriptive name
            3. **Supported account types**: Select 'Single tenant only'
            4. **Redirect URI**: Leave this blank
            5. Select **Register**
          `,
        },
      ],
    },
    {
      title: "Get client ID and tenant ID",
      items: [
        {
          kind: "markdown",
          content:
            "Navigate to the **Overview** of your created app and copy the following values:",
        },
        {
          kind: "input",
          label: "Application (client) ID",
          target: { kind: "oauthConnection", field: "clientId" },
          placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        },
        {
          kind: "input",
          label: "Directory (tenant) ID",
          target: { kind: "configuration", field: "tenantId" },
          placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        },
      ],
    },
    {
      title: "Create client secret",
      items: [
        {
          kind: "markdown",
          content: outdent`
            1. Select **Add a certificate or secret** from the overview page, then select **+ New client secret**
            2. Enter a description and select your preferred expiry
            3. Select **Add**
            4. Copy the **value** of the secret
          `,
        },
        {
          kind: "input",
          label: "Client secret value",
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Add API permissions",
      items: [
        {
          kind: "markdown",
          content: outdent`
            1. Navigate to **API permissions** and select **+ Add a permission**
            2. Select **APIs my organization uses**, then search for **WindowsDefenderATP**
            3. Select **Application permissions**
            4. Select your desired permissions (e.g. Machine.Read.All, Alert.Read.All) and select **Add permissions**

            Some permissions may require admin consent before they take effect.
          `,
        },
      ],
    },
    {
      title: "Specify your environment",
      items: [
        {
          kind: "select",
          label: "Environment",
          target: { kind: "configuration", field: "environment" },
          options: [
            {
              label: "Microsoft Defender for Endpoint global service",
              value: "global",
            },
            {
              label: "Microsoft Defender for Endpoint for US Government L4",
              value: "usgov-l4",
            },
            {
              label:
                "Microsoft Defender for Endpoint for US Government L5 (DOD)",
              value: "usgov-l5",
            },
          ],
          defaultValue: "global",
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    tokenEndpoint: ({ environment, tenantId }) => {
      const env = (environment as Environment) || "global";
      const config = environments[env];
      return `https://${config.loginDomain}/${tenantId}/oauth2/v2.0/token`;
    },
    scopes: ({ environment }) => {
      const env = (environment as Environment) || "global";
      const config = environments[env];
      return [`https://${config.apiDomain}/.default`];
    },
    clientCredentialsFrom: "db",
    tokenEndpointAuthMethod: () => "client_secret_post",
  },
  connectionTest: {
    protocol: "http",
    request: () => ({
      url: "https://api.securitycenter.microsoft.com/api/machines?$top=1",
      expectedStatus: [200, 403],
    }),
  },
});
